import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useClients } from '~/api/clients';
import { Separator, Title } from '~/components';
import { Field, Textarea } from '~/components/Form';
import { FormBrand, FormClient, FormClientReference, FormNumberingSeries, FormPaymentTerm, FormTitle } from '~/features/documents';

import type { CreateEditInvoiceFormType } from '../types';

import { FormEntryNumber } from '../../components/FormEntryNumber';
import { columnStyle, rowStyle, separatorStyle } from './InvoiceBasicSection.css';

type Props = {
  fcEntryNumber: null | string;
  form: UseFormReturn<CreateEditInvoiceFormType>;
  mode: 'create' | 'edit';
};

export const InvoiceBasicSection = ({ fcEntryNumber, form, mode }: Props) => {
  const { data: clients } = useClients();
  const { t } = useTranslation(['invoices', 'validation']);

  const { control, watch } = form;

  const watchClientId = watch('clientId');
  const selectedClient = clients.find(({ id }) => id === watchClientId) ?? null;
  const requiresClientReference = selectedClient?.requiresClientReference ?? false;

  return (
    <div className={rowStyle}>
      {/* Column 1 */}
      <div className={columnStyle}>
        <Title as="h2" styleAs="h3">
          {t('invoices:createEdit.basicInfo.title')}
        </Title>

        <FormTitle form={form} />

        {/* Description */}
        <Controller
          control={control}
          name="description"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label optional>{t('invoices:createEdit.assignment.fieldLabel')}</Field.Label>

              <Textarea
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('invoices:fields.description.placeholder')}
                ref={ref}
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            maxLength: {
              value: 2000,
              message: t('validation:maxLength', { attribute: t('invoices:createEdit.assignment.fieldLabel'), max: 2000 }),
            },
          }}
        />

        {/* Client */}
        <FormClient domain="invoices" form={form} />
      </div>

      <Separator className={separatorStyle} orientation="vertical" />

      {/* Column 2 */}
      <div className={columnStyle}>
        <Title as="h2" styleAs="h3">
          {t('invoices:createEdit.invoiceDetails.title')}
        </Title>

        {mode === 'create' ? <FormNumberingSeries enabledFor="invoices" form={form} /> : <FormEntryNumber entryNumber={fcEntryNumber} />}

        <FormPaymentTerm form={form} helpText="invoice" />

        <FormClientReference form={form} required={requiresClientReference} />

        {/* Brand */}
        <FormBrand form={form} />
      </div>
    </div>
  );
};
